import React, { useEffect, useRef } from "react"

import useProductTracking from "hooks/useProductTracking"
import { applyVideoCloudinaryTransforms } from "utils/applyCloudinaryTransforms"
import { formatCurrency } from "utils/currency"

import {
  Wrapper,
  Background,
  Container,
  VideoImageWrapper,
  Video,
  LargeImage,
  VideoProgress,
  VideoProgressBar,
  Content,
  SmallImage,
  Meta,
  TitleWrapper,
  Title,
  Description,
  BuyButton,
  CloseIcon,
  Arrow,
} from "./styles"

const VideoCarouselModalDesktop = ({
  elRef,
  shopifyProductVariant: { price, product },
  previewVideo: video,
  onArrowClick,
  arrowLeftDisabled,
  arrowRightDisabled,
  onCloseModal,
}) => {
  const { description, image, media, onlineStoreUrl, productId, title } = product

  const { trackProductClick } = useProductTracking()
  const refBuyButton = useRef()
  trackProductClick({
    elementRef: refBuyButton,
    item: {
      itemType: "Product",
      itemId: productId,
      shopifyProductVariant: { price, product: { title } },
    },
  })

  const refVideo = useRef(null)
  const refProgressBar = useRef(null)

  const handlePlayPause = () => {
    if (refVideo.current.paused) {
      refVideo.current.play()
    } else {
      refVideo.current.pause()
    }
  }

  const handleProgress = () => {
    if (refVideo.current && refProgressBar.current) {
      const progressPercentage = (refVideo.current.currentTime / refVideo.current.duration) * 100
      refProgressBar.current.style.width = `${progressPercentage}%`
    }
  }

  useEffect(() => {
    if (refVideo.current && refProgressBar.current) {
      refVideo.current.addEventListener("timeupdate", handleProgress)
    }

    return () => {
      if (refVideo.current && refProgressBar.current) {
        refVideo.current.removeEventListener("timeupdate", handleProgress)
      }
    }
  }, [video])

  return (
    <Wrapper ref={elRef} onClick={onCloseModal}>
      <Background />
      <Container onClick={(event) => event.stopPropagation()}>
        <VideoImageWrapper>
          {video ? (
            <>
              <Video
                ref={refVideo}
                width="100%"
                autoPlay
                muted={false}
                loop
                playsInline
                type="video/mp4"
                src={applyVideoCloudinaryTransforms(
                  video.videoObject[0].secure_url,
                  `c_fill,w_475,h_675`,
                )}
                onClick={handlePlayPause}
              />
              <VideoProgress>
                <VideoProgressBar ref={refProgressBar} />
              </VideoProgress>
            </>
          ) : (
            <LargeImage
              baseUrl={image || media[0]?.image.originalSrc}
              images={[{ transforms: "c_fill,g_auto,w_475,h_675,q_auto,f_auto,fl_lossy" }]}
            />
          )}
        </VideoImageWrapper>
        <Content>
          <SmallImage
            baseUrl={image || media[0]?.image.originalSrc}
            images={[{ transforms: "c_fill,g_auto,w_100,h_100,q_auto,f_auto,fl_lossy" }]}
          />
          <Meta>
            <TitleWrapper>
              <Title>{title}</Title>
              <Title>{formatCurrency(price, true)}</Title>
            </TitleWrapper>
            {description && <Description>{description}</Description>}
            <a
              ref={refBuyButton}
              href={onlineStoreUrl}
              title={title}
              target="_blank"
              rel="noreferrer"
            >
              <BuyButton>BUY NOW</BuyButton>
            </a>
          </Meta>
        </Content>
        <CloseIcon id="close-icon" width={17} height={17} onClick={onCloseModal} />
        <Arrow
          direction="left"
          onClick={() => onArrowClick("left")}
          tabIndex={-1}
          disabled={arrowLeftDisabled}
          ismodal="true"
        />
        <Arrow
          direction="right"
          onClick={() => onArrowClick("right")}
          tabIndex={-1}
          disabled={arrowRightDisabled}
          ismodal="true"
        />
      </Container>
    </Wrapper>
  )
}

export default VideoCarouselModalDesktop
